import { supabase } from '../lib/supabase';

export const waitlistService = {
  /**
   * Add new signup to waitlist
   */
  async addSignup(signupData) {
    try {
      const { data, error } = await supabase?.from('waitlist_signups')?.insert([{
          email: signupData?.email,
          first_name: signupData?.firstName,
          family_size: signupData?.familySize || null,
          referral_source: signupData?.referralSource || 'website',
          newsletter_consent: signupData?.newsletterConsent ?? true,
          metadata: signupData?.metadata || {}
        }])?.select()?.single()

      if (error) {
        throw error
      }

      return { data, error: null }
    } catch (error) {
      if (error?.code === '23505' || error?.message?.includes('duplicate')) {
        return { 
          data: null, 
          error: { message: 'This email is already on our waitlist! We will notify you when Cynizee is ready.' }
        }
      }
      return { data: null, error }
    }
  },

  /**
   * Send email notification via Edge Function with enhanced debugging
   */
  async sendWelcomeEmail(signupData) {
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY

      console.log('🔧 DEBUG: Attempting to send welcome email', {
        recipient: signupData?.email,
        firstName: signupData?.firstName,
        supabaseUrl: supabaseUrl ? 'Configured ✅' : 'Missing ❌'
      });

      const response = await fetch(`${supabaseUrl}/functions/v1/send-waitlist-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          type: 'waitlist_signup',
          to: signupData?.email,
          firstName: signupData?.firstName,
          familySize: signupData?.familySize,
          metadata: signupData?.metadata || {},
          debug: true // Enable debug mode
        })
      })

      const result = await response?.json()
      
      console.log('📧 Email API Response:', {
        status: response?.status,
        statusText: response?.statusText,
        success: response?.ok,
        result: result
      });
      
      if (!response?.ok) {
        console.error('❌ Email sending failed:', result);
        throw new Error(result?.error || 'Failed to send email notification')
      }

      console.log('✅ Welcome email sent successfully:', result?.messageId);
      return { data: result, error: null }
    } catch (error) {
      console.error('🚨 Welcome email error:', error);
      return { data: null, error }
    }
  },

  /**
   * Send admin notification about new signup with enhanced debugging
   */
  async notifyAdmin(signupData) {
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY

      console.log('🔔 DEBUG: Attempting to send admin notification', {
        newSignup: signupData?.email,
        firstName: signupData?.firstName
      });

      const response = await fetch(`${supabaseUrl}/functions/v1/send-waitlist-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          type: 'admin_notification',
          email: signupData?.email,
          firstName: signupData?.firstName,
          familySize: signupData?.familySize,
          signupDate: new Date()?.toISOString(),
          metadata: signupData?.metadata || {},
          debug: true
        })
      })

      const result = await response?.json()
      
      console.log('📬 Admin notification response:', {
        status: response?.status,
        success: response?.ok,
        result: result
      });
      
      if (!response?.ok) {
        console.error('❌ Admin notification failed:', result?.error)
        // Don't throw error for admin notifications - user signup should still succeed
      } else {
        console.log('✅ Admin notification sent successfully:', result?.messageId);
      }

      return { data: result, error: null }
    } catch (error) {
      console.error('🚨 Admin notification error:', error)
      // Don't throw error for admin notifications
      return { data: null, error }
    }
  },

  /**
   * Get waitlist statistics (for admin use)
   */
  async getStats() {
    try {
      const { data, error } = await supabase?.rpc('get_waitlist_stats')

      if (error) {
        throw error
      }

      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  /**
   * Get recent signups (for admin use)
   */
  async getRecentSignups(limit = 50) {
    try {
      const { data, error } = await supabase?.from('waitlist_signups')?.select('*')?.order('signup_date', { ascending: false })?.limit(limit)

      if (error) {
        throw error
      }

      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  /**
   * Get signups that need welcome emails (retroactive functionality)
   */
  async getPendingWelcomeEmails(limit = 50) {
    try {
      const { data, error } = await supabase?.rpc('get_pending_welcome_emails', { limit_count: limit })

      if (error) {
        throw error
      }

      console.log(`📧 Found ${data?.length || 0} signups needing welcome emails`);
      return { data, error: null }
    } catch (error) {
      console.error('🚨 Error fetching pending welcome emails:', error);
      return { data: null, error }
    }
  },

  /**
   * Get signups that need admin notifications (retroactive functionality)
   */
  async getPendingAdminNotifications(limit = 50) {
    try {
      const { data, error } = await supabase?.rpc('get_pending_admin_notifications', { limit_count: limit })

      if (error) {
        throw error
      }

      console.log(`📬 Found ${data?.length || 0} signups needing admin notifications`);
      return { data, error: null }
    } catch (error) {
      console.error('🚨 Error fetching pending admin notifications:', error);
      return { data: null, error }
    }
  },

  /**
   * Send retroactive welcome emails to past signups
   */
  async sendRetroactiveWelcomeEmails() {
    try {
      console.log('🔄 Starting retroactive welcome email process...');
      
      const pendingEmails = await this.getPendingWelcomeEmails(25); // Process in batches
      
      if (!pendingEmails?.data?.length) {
        console.log('✅ No pending welcome emails found');
        return { data: { processed: 0, successful: 0, failed: 0 }, error: null };
      }

      const results = {
        processed: pendingEmails?.data?.length,
        successful: 0,
        failed: 0,
        details: []
      };

      // Process each signup
      for (const signup of pendingEmails?.data) {
        console.log(`📧 Sending retroactive welcome email to ${signup?.email}...`);
        
        const emailResult = await this.sendWelcomeEmail({
          email: signup?.email,
          firstName: signup?.first_name,
          familySize: signup?.family_size,
          metadata: { ...signup?.metadata, retroactive: true }
        });

        // Mark email as sent (success or failure)
        const markResult = await this.markEmailAsSent(
          signup?.id, 
          'welcome', 
          !emailResult?.error,
          emailResult?.error?.message
        );

        if (!emailResult?.error && markResult?.data) {
          results.successful++;
          console.log(`✅ Retroactive welcome email sent to ${signup?.email}`);
        } else {
          results.failed++;
          console.log(`❌ Failed to send retroactive email to ${signup?.email}:`, emailResult?.error?.message);
        }

        results?.details?.push({
          email: signup?.email,
          success: !emailResult?.error,
          error: emailResult?.error?.message,
          signupDate: signup?.signup_date
        });

        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log(`🎯 Retroactive email process complete:`, results);
      return { data: results, error: null };

    } catch (error) {
      console.error('🚨 Retroactive welcome email process failed:', error);
      return { data: null, error };
    }
  },

  /**
   * Send retroactive admin notifications for past signups
   */
  async sendRetroactiveAdminNotifications() {
    try {
      console.log('🔔 Starting retroactive admin notification process...');
      
      const pendingNotifications = await this.getPendingAdminNotifications(25);
      
      if (!pendingNotifications?.data?.length) {
        console.log('✅ No pending admin notifications found');
        return { data: { processed: 0, successful: 0, failed: 0 }, error: null };
      }

      const results = {
        processed: pendingNotifications?.data?.length,
        successful: 0,
        failed: 0,
        details: []
      };

      // Process each signup
      for (const signup of pendingNotifications?.data) {
        console.log(`📬 Sending retroactive admin notification for ${signup?.email}...`);
        
        const notificationResult = await this.notifyAdmin({
          email: signup?.email,
          firstName: signup?.first_name,
          familySize: signup?.family_size,
          metadata: { retroactive: true, originalSignupDate: signup?.signup_date }
        });

        // Mark notification as sent
        const markResult = await this.markEmailAsSent(
          signup?.id, 
          'admin', 
          !notificationResult?.error,
          notificationResult?.error?.message
        );

        if (!notificationResult?.error && markResult?.data) {
          results.successful++;
          console.log(`✅ Retroactive admin notification sent for ${signup?.email}`);
        } else {
          results.failed++;
          console.log(`❌ Failed to send retroactive admin notification for ${signup?.email}`);
        }

        results?.details?.push({
          email: signup?.email,
          success: !notificationResult?.error,
          error: notificationResult?.error?.message,
          signupDate: signup?.signup_date
        });

        // Add small delay
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log(`🎯 Retroactive admin notification process complete:`, results);
      return { data: results, error: null };

    } catch (error) {
      console.error('🚨 Retroactive admin notification process failed:', error);
      return { data: null, error };
    }
  },

  /**
   * Mark email as sent in database
   */
  async markEmailAsSent(signupId, emailType, success = true, errorMessage = null) {
    try {
      const { data, error } = await supabase?.rpc('mark_waitlist_email_sent', {
        signup_uuid: signupId,
        email_type: emailType,
        success: success,
        error_message: errorMessage
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error(`🚨 Error marking ${emailType} email as sent:`, error);
      return { data: null, error };
    }
  },

  /**
   * Get email statistics and tracking info
   */
  async getEmailStats() {
    try {
      const { data, error } = await supabase?.rpc('get_waitlist_email_stats');

      if (error) {
        throw error;
      }

      return { data: data?.[0] || {}, error: null };
    } catch (error) {
      console.error('🚨 Error fetching email stats:', error);
      return { data: null, error };
    }
  },

  /**
   * Process all retroactive emails (both welcome and admin)
   */
  async processAllRetroactiveEmails() {
    try {
      console.log('🚀 Starting complete retroactive email processing...');
      
      // Get current stats first
      const statsResult = await this.getEmailStats();
      console.log('📊 Current email stats:', statsResult?.data);

      // Process welcome emails first
      const welcomeResults = await this.sendRetroactiveWelcomeEmails();
      
      // Then process admin notifications
      const adminResults = await this.sendRetroactiveAdminNotifications();

      // Get updated stats
      const updatedStatsResult = await this.getEmailStats();
      
      const summary = {
        welcomeEmails: welcomeResults?.data,
        adminNotifications: adminResults?.data,
        beforeStats: statsResult?.data,
        afterStats: updatedStatsResult?.data,
        timestamp: new Date()?.toISOString()
      };

      console.log('🎉 Complete retroactive email processing finished:', summary);
      return { data: summary, error: null };

    } catch (error) {
      console.error('🚨 Complete retroactive email processing failed:', error);
      return { data: null, error };
    }
  },

  /**
   * Test email functionality for debugging
   */
  async testEmailSystem() {
    try {
      const testData = {
        email: 'test@example.com',
        firstName: 'Test User',
        familySize: '4',
        metadata: { test: true }
      };

      console.log('🧪 Testing email system...');
      
      // Test welcome email
      const welcomeTest = await this.sendWelcomeEmail(testData);
      
      // Test admin notification
      const adminTest = await this.notifyAdmin(testData);

      return {
        welcomeEmailTest: welcomeTest,
        adminNotificationTest: adminTest,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('🚨 Email system test failed:', error);
      return { error: error?.message };
    }
  }
}

export default waitlistService